/**
 * Dashboard — Tagesübersicht
 */
import { state, formatCash } from '../main';
import { fixture, mapData, type ActionReportEntry } from '../data/fixture';
import { t } from '../i18n';
import { markReady } from '../api';
import { openCorpDossier } from './corp-dossier';
import { getJwt, parseJwt } from '../auth';
import { balanceCategoryLabel } from '../utils/balance-label';
import { dialTile, formatCashCompact } from '../utils/ui';

declare const __APP_VERSION__: string;

// ── JSON Export Helpers ───────────────────────────────────────────────

function downloadJSON(data: unknown, filename: string): void {
  const json = JSON.stringify(data, null, 2);
  const blob = new Blob([json], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function copyJSON(data: unknown): void {
  const json = JSON.stringify(data, null, 2);
  navigator.clipboard.writeText(json).catch(err => console.error('[CORP//LLM] clipboard write failed', err));
}

function isAdmin(): boolean {
  const jwt = getJwt();
  return !!(jwt && parseJwt(jwt)?.admin);
}

function buildPlanExport(): Record<string, unknown> {
  const plan = fixture.player?.plan ?? [];
  return {
    _export: 'plan',
    corp: state.corpName,
    session: state.sessionId,
    cycle: state.cycle + 1,
    timestamp: new Date().toISOString(),
    clientVersion: typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : 'dev',
    actions: plan.length,
    runners: [...new Set(plan.map(a => a.runner))].length,
    plan,
  };
}

function buildResultsExport(): Record<string, unknown> {
  const report: ActionReportEntry[] = fixture.player?.actionReport ?? [];
  const balance = fixture.player?.dailyBalance;
  const totalCash = report.reduce((s, e) => s + e.deltaCash, 0);
  const totalHeat = report.reduce((s, e) => s + e.deltaHeat, 0);
  const successes = report.filter(e => e.result === 'SUCCESS' || e.result === 'CRIT_SUCCESS').length;
  return {
    _export: 'results',
    corp: state.corpName,
    session: state.sessionId,
    cycle: state.cycle,
    timestamp: new Date().toISOString(),
    clientVersion: typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : 'dev',
    summary: {
      totalActions: report.length,
      successes,
      failures: report.length - successes,
      successRate: report.length > 0 ? Math.round(successes / report.length * 100) : 0,
      totalCashDelta: totalCash,
      totalHeatDelta: totalHeat,
    },
    actionReport: report,
    dailyBalance: balance ?? null,
  };
}

function buildDebugSnapshot(): Record<string, unknown> {
  return {
    _export: 'debug_snapshot',
    corp: state.corpName,
    session: state.sessionId,
    cycle: state.cycle,
    timestamp: new Date().toISOString(),
    clientVersion: typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : 'dev',
    state: {
      cleanCash: state.cleanCash,
      dirtyCash: state.dirtyCash,
      cycle: state.cycle,
      corpName: state.corpName,
      notifications: state.notifications,
    },
    player: fixture.player ?? null,
    sectors: fixture.sectors ?? [],
    leaderboard: fixture.leaderboard ?? [],
    gazette: fixture.gazette ?? [],
    readyPlayers: fixture.readyPlayers ?? [],
    totalPlayers: fixture.totalPlayers ?? 0,
    playerReady: fixture.playerReady ?? false,
    lastTickAt: fixture.lastTickAt ?? null,
  };
}

function exportFilename(type: string): string {
  const corp = (state.corpName ?? 'unknown').replace(/[^a-zA-Z0-9_-]/g, '_');
  const cycle = state.cycle;
  const session = (state.sessionId ?? '').slice(0, 8);
  return `corpllm_${type}_${corp}_cycle-${cycle}_${session}.json`;
}

function skippedReason(code: string): string {
  const map: Record<string, string> = {
    'SKIPPED:ARRESTED':      t('dashboard.skip_arrested'),
    'SKIPPED:IN_COMBAT':     t('dashboard.skip_in_combat'),
    'SKIPPED:COOLDOWN':      t('dashboard.skip_cooldown'),
    'SKIPPED:MAX_PER_CYCLE': t('dashboard.skip_max_per_cycle'),
    'SKIPPED:PREREQ_ATTR':   t('dashboard.skip_prereq_attr'),
    'SKIPPED:NO_FUNDS':      t('dashboard.skip_no_funds'),
  };
  return map[code] ?? t('dashboard.skip_default');
}

function reportResultColor(r: string): string {
  if (r.startsWith('SKIPPED:')) return '#6a7a8a';
  switch (r) {
    case 'CRIT_SUCCESS': return 'var(--cyan)';
    case 'SUCCESS': return 'var(--green)';
    case 'FAIL': return 'var(--red)';
    case 'CRIT_FAIL': return '#ff4444';
    case 'ARRESTED': return '#ff6b6b';
    default: return 'var(--text-mid)';
  }
}

function reportResultLabel(r: string): string {
  if (r.startsWith('SKIPPED:')) return skippedReason(r);
  switch (r) {
    case 'CRIT_SUCCESS': return t('dashboard.report_crit_success');
    case 'SUCCESS': return t('dashboard.report_success');
    case 'FAIL': return t('dashboard.report_fail');
    case 'CRIT_FAIL': return t('dashboard.report_crit_fail');
    case 'ARRESTED': return t('dashboard.report_arrested');
    default: return r;
  }
}

function isSuccess(r: string): boolean {
  return r === 'SUCCESS' || r === 'CRIT_SUCCESS';
}

function buildingName(nodeId: string): string {
  const b = mapData?.worldBuildings?.find(w => w.node_id === nodeId);
  return b?.display_name ?? nodeId;
}

function sectorFromTarget(target?: string): string {
  return target?.replace(/_BE_\d+$/, '') ?? '?';
}

function entryDeltaHTML(e: ActionReportEntry): string {
  const cashStr = e.deltaCash !== 0
    ? `<span style="color:${e.deltaCash > 0 ? 'var(--green)' : 'var(--red)'}; margin-left:6px">${e.deltaCash > 0 ? '+' : ''}${formatCash(e.deltaCash)}</span>`
    : '';
  const heatStr = e.deltaHeat !== 0
    ? `<span style="color:var(--amber); margin-left:6px">⚡${e.deltaHeat > 0 ? '+' : ''}${Math.round(e.deltaHeat)}</span>`
    : '';
  return cashStr + heatStr;
}

function buildSingleRow(e: ActionReportEntry): string {
  const color = reportResultColor(e.result);
  const icon = isSuccess(e.result) ? '✓' : e.result.startsWith('SKIPPED:') ? '⊘' : '✗';
  const target = e.target ? `<span style="color:var(--text-lo); margin-left:6px; font-size:11px">${e.target}</span>` : '';
  return `<div style="display:flex; align-items:center; gap:8px; padding:4px 8px; border-bottom:1px solid rgba(255,255,255,0.04)">
    <span style="color:${color}; width:14px; text-align:center; font-weight:bold">${icon}</span>
    <span style="color:var(--text-hi); min-width:80px">${e.runner}</span>
    <span style="color:var(--text-mid); flex:1">${e.action}${target}</span>
    <span style="color:${color}; font-size:12px; font-weight:600; min-width:100px; text-align:right">${reportResultLabel(e.result)}</span>
    ${entryDeltaHTML(e)}
  </div>`;
}

function buildGroupHTML(entries: ActionReportEntry[]): string {
  const first = entries[0];
  const ok = entries.filter(e => isSuccess(e.result)).length;
  const total = entries.length;
  const totalCash = entries.reduce((s, e) => s + e.deltaCash, 0);
  const totalHeat = entries.reduce((s, e) => s + e.deltaHeat, 0);
  const sector = sectorFromTarget(first.target);
  const allOk = ok === total;
  const noneOk = ok === 0;
  const summaryColor = allOk ? 'var(--green)' : noneOk ? 'var(--red)' : 'var(--amber)';

  const cashSum = totalCash !== 0
    ? `<span style="color:${totalCash > 0 ? 'var(--green)' : 'var(--red)'}">${totalCash > 0 ? '+' : ''}${formatCash(totalCash)}</span>`
    : '';
  const heatSum = totalHeat !== 0
    ? `<span style="color:var(--amber)">⚡${totalHeat > 0 ? '+' : ''}${Math.round(totalHeat)}</span>`
    : '';
  const statsStr = [cashSum, heatSum].filter(Boolean).join(', ');

  // Build child rows — RECRUIT with targetNpc gets nested (Building → NPC), rest is flat
  const hasNpcs = entries.some(e => e.targetNpc);
  let children: string;

  if (hasNpcs) {
    // Group entries by building, then list NPCs under each building
    const byBuilding = new Map<string, ActionReportEntry[]>();
    for (const e of entries) {
      const key = e.target ?? '?';
      const arr = byBuilding.get(key);
      if (arr) arr.push(e); else byBuilding.set(key, [e]);
    }
    const bldKeys = [...byBuilding.keys()];
    children = bldKeys.map((bKey, bi) => {
      const bEntries = byBuilding.get(bKey)!;
      const isLastBld = bi === bldKeys.length - 1;
      const bConn = isLastBld ? '└' : '├';
      const bName = buildingName(bKey);
      const bOk = bEntries.filter(e => isSuccess(e.result)).length;
      const npcRows = bEntries.map((e, ni) => {
        const isLastNpc = ni === bEntries.length - 1;
        const nConn = isLastNpc ? '└' : '├';
        const vertLine = isLastBld ? ' ' : '│';
        const color = reportResultColor(e.result);
        const icon = isSuccess(e.result) ? '✓' : '✗';
        return `<div style="display:flex; align-items:center; gap:6px; padding:1px 8px 1px 40px; font-size:11px; color:var(--text-lo)">
          <span style="width:10px; font-family:var(--mono)">${vertLine}</span>
          <span style="width:10px; font-family:var(--mono)">${nConn}</span>
          <span style="color:${color}; width:12px; text-align:center; font-weight:bold">${icon}</span>
          <span style="color:var(--text-mid); flex:1">${e.targetNpc ?? '?'}</span>
          <span style="color:${color}; min-width:80px; text-align:right">${reportResultLabel(e.result)}</span>
          ${entryDeltaHTML(e)}
        </div>`;
      }).join('');
      return `<div style="display:flex; align-items:center; gap:6px; padding:2px 8px 2px 28px; font-size:11px; color:var(--text-mid)">
        <span style="color:var(--text-lo); width:10px; font-family:var(--mono)">${bConn}</span>
        <span style="flex:1">${bName} <span style="color:var(--text-lo)">(${bOk}/${bEntries.length})</span></span>
      </div>${npcRows}`;
    }).join('');
  } else {
    // Flat list: one row per building
    children = entries.map((e, i) => {
      const isLast = i === entries.length - 1;
      const connector = isLast ? '└' : '├';
      const color = reportResultColor(e.result);
      const icon = isSuccess(e.result) ? '✓' : '✗';
      const label = e.target ? buildingName(e.target) : '?';
      return `<div style="display:flex; align-items:center; gap:6px; padding:2px 8px 2px 28px; font-size:11px; color:var(--text-lo)">
        <span style="color:var(--text-lo); width:10px; font-family:var(--mono)">${connector}</span>
        <span style="color:${color}; width:12px; text-align:center; font-weight:bold">${icon}</span>
        <span style="color:var(--text-mid); flex:1">${label}</span>
        <span style="color:${color}; min-width:80px; text-align:right">${reportResultLabel(e.result)}</span>
        ${entryDeltaHTML(e)}
      </div>`;
    }).join('');
  }

  const uid = first.groupId ?? Math.random().toString(36).slice(2);

  return `<div style="border-bottom:1px solid rgba(255,255,255,0.04)">
    <div class="report-group-toggle" data-group="${uid}"
         style="display:flex; align-items:center; gap:8px; padding:4px 8px; cursor:pointer; user-select:none"
         onclick="this.classList.toggle('open'); this.nextElementSibling.style.display = this.classList.contains('open') ? 'block' : 'none'; this.querySelector('.report-chevron').textContent = this.classList.contains('open') ? '▼' : '▶'">
      <span class="report-chevron" style="color:var(--text-lo); width:14px; text-align:center; font-size:10px">▶</span>
      <span style="color:var(--text-hi); min-width:80px">${first.runner}</span>
      <span style="color:var(--text-mid); flex:1">${first.action} <span style="color:var(--text-lo); font-size:11px">${sector}</span></span>
      <span style="color:${summaryColor}; font-size:12px; font-weight:600; min-width:100px; text-align:right">${ok}/${total} ${t('dashboard.report_success_count')}</span>
      <span style="font-size:11px">${statsStr}</span>
    </div>
    <div style="display:none; background:rgba(0,0,0,0.15)">${children}</div>
  </div>`;
}

function buildActionReportHTML(): string {
  const report: ActionReportEntry[] = fixture.player?.actionReport ?? [];
  if (report.length === 0) return '';

  // Separate grouped vs ungrouped entries
  const groups = new Map<string, ActionReportEntry[]>();
  const ungrouped: ActionReportEntry[] = [];

  for (const e of report) {
    if (e.groupId) {
      const arr = groups.get(e.groupId);
      if (arr) arr.push(e); else groups.set(e.groupId, [e]);
    } else {
      ungrouped.push(e);
    }
  }

  // Render: groups with >1 entry get collapsible view, single-entry groups + ungrouped stay flat
  const rows: string[] = [];
  const seen = new Set<string>();

  for (const e of report) {
    if (e.groupId) {
      if (seen.has(e.groupId)) continue;
      seen.add(e.groupId);
      const grp = groups.get(e.groupId)!;
      rows.push(grp.length > 1 ? buildGroupHTML(grp) : buildSingleRow(grp[0]));
    } else {
      rows.push(buildSingleRow(e));
    }
  }

  const totalEntries = report.length;
  const successCount = report.filter(e => isSuccess(e.result)).length;

  const autoRepeated = fixture.player?.planAutoRepeated === true;

  return `<div class="panel" style="margin-bottom:16px">
    <div class="panel-title" style="display:flex; justify-content:space-between; align-items:center">
      <span>${t('dashboard.report_title')}${autoRepeated ? ` <span class="dash-auto-repeat-badge" style="font-size:10px;font-weight:normal;padding:1px 6px;border-radius:3px;background:rgba(252,163,17,0.12);border:1px solid rgba(252,163,17,0.3);color:#fca311;margin-left:8px">${t('dashboard.auto_repeated')}</span>` : ''}</span>
      <span style="display:flex; align-items:center; gap:8px">
        <span style="font-size:11px; color:var(--text-lo); font-weight:normal">${successCount}/${totalEntries} ${t('dashboard.report_success_count')}</span>
        <button class="dash-export-btn" id="btn-export-results" title="Ergebnis als JSON exportieren">⬇</button>
        <button class="dash-export-btn" id="btn-copy-results" title="Ergebnis als JSON kopieren">📋</button>
      </span>
    </div>
    <div style="font-family:var(--mono); font-size:12px">${rows.join('')}</div>
  </div>`;
}

export function renderDashboard(): HTMLElement {
  const root = document.createElement('div');
  root.className = 'screen';

  // Daily balance from fixture (live data)
  const balance   = fixture.player?.dailyBalance;
  const totalInc  = balance?.income.reduce((s, l) => s + l.amount, 0) ?? 0;
  const totalExp  = balance?.expenses.reduce((s, l) => s + l.amount, 0) ?? 0;
  const netDay    = totalInc - totalExp;
  const netColor  = netDay >= 0 ? 'var(--green)' : 'var(--red)';
  const netSign   = netDay >= 0 ? '+' : '';

  // Runner counts from fixture
  const runners       = fixture.player?.runners ?? [];
  const activeCount   = runners.filter(r => r.status === 'active').length;
  const arrestedCount = runners.filter(r => r.status === 'arrested').length;
  const injuredCount  = runners.filter(r => r.status === 'injured').length;
  const managerCount  = (fixture.player?.managers ?? []).length;

  // Territorial control from sectors
  const sectors       = fixture.sectors ?? [];
  const ownedSectors  = sectors.filter(s => s.owner === state.corpName).length;
  const totalSectors  = sectors.length;

  // Runway estimate
  const totalCash  = state.cleanCash + state.dirtyCash;
  const runwayDays = netDay > 0 ? Math.round(totalCash / netDay) : null;

  // Readiness state
  const readyCorps   = fixture.readyPlayers ?? [];
  const totalPlayers = fixture.totalPlayers ?? 0;
  const readyCount   = readyCorps.length;
  const isReady      = fixture.playerReady ?? false;
  const pct          = totalPlayers > 0 ? Math.round(readyCount / totalPlayers * 100) : 0;

  // Countdown to auto-resolve (24h from lastTickAt)
  let countdownStr = '';
  if (fixture.lastTickAt) {
    const deadline = new Date(fixture.lastTickAt).getTime() + 24 * 60 * 60 * 1000;
    const remaining = deadline - Date.now();
    if (remaining > 0) {
      const h = Math.floor(remaining / 3_600_000);
      const m = Math.floor((remaining % 3_600_000) / 60_000);
      countdownStr = `${h}h ${m}m`;
    }
  }

  // Corp status chips
  const leaderboard = fixture.leaderboard ?? [];
  const allCorps = leaderboard.map(c => c.name);
  const corpChips = allCorps.map(name => {
    const ready = readyCorps.includes(name);
    const isMe = name === state.corpName;
    return `<span class="ready-chip chip corp-link ${ready ? 'ready' : ''} ${isMe ? 'me' : ''}" data-corp="${name}" style="cursor:pointer">${ready ? '✓' : '○'} ${name}</span>`;
  }).join('');

  // ── Hero metrics (KPI row + status bar) ──────────────────────────────
  const netWorthVal = state.netWorth || totalCash;
  const nwHist   = fixture.player?.netWorthHistory ?? [];
  const nwPrev   = nwHist.length >= 2 ? nwHist[nwHist.length - 2] : null;
  const nwTrend  = (nwPrev != null && nwPrev > 0) ? Math.round((netWorthVal - nwPrev) / nwPrev * 1000) / 10 : null;
  const nwTrendStr = nwTrend != null ? `${nwTrend >= 0 ? '▲ +' : '▼ '}${nwTrend}% / ${t('topbar.cycle')}` : '—';
  const incSources = (balance?.income ?? []).length;
  const expCats    = (balance?.expenses ?? []).length;
  const heat       = Math.round(state.federalHeat);
  const readinessPct = runners.length > 0 ? Math.round(activeCount / runners.length * 100) : 0;
  const sectorPct  = totalSectors > 0 ? Math.round(ownedSectors / totalSectors * 100) : 0;
  const myStanding = leaderboard.find(c => c.isMe) ?? leaderboard.find(c => c.name === state.corpName);
  const myRank     = myStanding?.rank ?? null;
  const runwayStr  = runwayDays != null ? `~${runwayDays} ${t('topbar.cycle').toLowerCase()}` : '—';
  // Konzern-Status: derived from real heat + cashflow (not a fabricated field)
  let statusLabel: string, statusColor: string;
  if (heat >= 80)                    { statusLabel = t('dashboard.status_critical');    statusColor = 'var(--red)'; }
  else if (heat >= 50 || netDay < 0) { statusLabel = t('dashboard.status_strained');    statusColor = 'var(--amber)'; }
  else                               { statusLabel = t('dashboard.status_operational'); statusColor = 'var(--green)'; }

  root.innerHTML = `
    <div class="screen-title">${t('dashboard.title', { cycle: state.cycle })}</div>

    <!-- Status bar -->
    <div class="dash-statusbar">
      <div class="dash-sb-cell"><span class="dash-sb-lbl">${t('dashboard.konzern_status')}</span><span class="dash-sb-val" style="color:${statusColor}">${statusLabel}</span></div>
      <div class="dash-sb-cell"><span class="dash-sb-lbl">${t('dashboard.sector_control')}</span><span class="dash-sb-val">${ownedSectors} / ${totalSectors}${totalSectors > 0 ? ` · ${sectorPct}%` : ''}</span></div>
      <div class="dash-sb-cell"><span class="dash-sb-lbl">${t('dashboard.active_runners')}</span><span class="dash-sb-val">${activeCount} / ${runners.length}</span></div>
      <div class="dash-sb-cell"><span class="dash-sb-lbl">${t('dashboard.rank')}</span><span class="dash-sb-val">${myRank != null ? `#${myRank} / ${leaderboard.length}` : '—'}</span></div>
    </div>

    <!-- KPI hero row -->
    <div class="kpi-row">
      <div class="kpi amber"><span class="k-lbl">${t('dashboard.net_worth')}</span><span class="k-val" title="${formatCash(netWorthVal)}">${formatCashCompact(netWorthVal)}</span><span class="k-sub">${nwTrendStr}</span><div class="spark"></div></div>
      <div class="kpi ${netDay >= 0 ? 'pos' : 'neg'}"><span class="k-lbl">${t('dashboard.net_per_day')}</span><span class="k-val" title="${formatCash(netDay)}">${netSign}${formatCashCompact(netDay)}</span><span class="k-sub">${t('dashboard.runway')}: ${runwayStr}</span></div>
      <div class="kpi cyan"><span class="k-lbl">${t('dashboard.income')}</span><span class="k-val" title="${formatCash(totalInc)}">+${formatCashCompact(totalInc)}</span><span class="k-sub">${incSources} ${t('dashboard.kpi_sources')}</span></div>
      <div class="kpi neg"><span class="k-lbl">${t('dashboard.expenses')}</span><span class="k-val" title="${formatCash(totalExp)}">−${formatCashCompact(totalExp)}</span><span class="k-sub">${expCats} ${t('dashboard.kpi_items')}</span></div>
      ${dialTile(heat, 'amber', t('dashboard.federal_heat'), String(heat))}
      ${dialTile(readinessPct, 'cyan', t('dashboard.readiness'), readinessPct + '%')}
    </div>

    <!-- Readiness Panel -->
    <div class="panel" id="ready-panel" style="margin-bottom:16px; border-color:${isReady ? 'rgba(52,211,153,0.3)' : 'var(--border)'}">
      <div style="display:flex; align-items:center; gap:16px; flex-wrap:wrap">
        <button class="ready-btn btn cyan ${isReady ? 'confirmed' : ''}" id="btn-ready" ${isReady ? 'disabled' : ''}>
          ${isReady ? t('dashboard.ready_confirmed') : t('dashboard.ready_btn')}
        </button>
        <div style="flex:1; min-width:200px">
          <div style="display:flex; justify-content:space-between; margin-bottom:4px">
            <span id="ready-count" style="font-size:13px; color:var(--text-mid)">${t('dashboard.ready_count', { ready: readyCount, total: totalPlayers })}</span>
            ${countdownStr ? `<span id="ready-countdown" style="font-size:11px; color:var(--text-lo)">${t('dashboard.ready_timeout', { time: countdownStr })}</span>` : ''}
          </div>
          <div class="ready-bar"><div class="ready-bar-fill" id="ready-bar-fill" style="width:${pct}%"></div></div>
        </div>
      </div>
      ${totalPlayers > 0 ? `<div style="display:flex; gap:8px; flex-wrap:wrap; margin-top:10px" id="ready-corps">${corpChips}</div>` : ''}
    </div>

    <!-- Row 1: Warnungen -->
    <div class="panel" style="margin-bottom:16px">
      <div class="panel-title">${t('dashboard.warnings')}</div>
      <div id="dash-warnings"></div>
    </div>

    <!-- Zyklusbericht -->
    ${buildActionReportHTML()}

    <!-- Row 2: 3 columns -->
    <div style="display:grid; grid-template-columns:1fr 1fr 1fr; gap:16px; margin-bottom:16px">

      <!-- Tages-Finanzen -->
      <div class="panel">
        <div class="panel-title">${t('dashboard.daily_finance')}</div>
        <div class="dash-fin-row" style="color:var(--green)">
          <span>${t('dashboard.income')}</span><span>+${formatCash(totalInc)}</span>
        </div>
        ${(balance?.income ?? []).map(l =>
          `<div class="dash-fin-row dash-sub"><span>\u251C ${balanceCategoryLabel(l)}</span><span>${formatCash(l.amount)}</span></div>`
        ).join('')}
        <div class="dash-divider"></div>
        <div class="dash-fin-row" style="color:var(--red)">
          <span>${t('dashboard.expenses')}</span><span>\u2212${formatCash(totalExp)}</span>
        </div>
        ${(balance?.expenses ?? []).map(l =>
          `<div class="dash-fin-row dash-sub"><span>\u251C ${balanceCategoryLabel(l)}</span><span>${formatCash(l.amount)}</span></div>`
        ).join('')}
        <div class="dash-divider"></div>
        <div class="dash-fin-row" style="color:${netColor}; font-weight:600">
          <span>${t('dashboard.net_per_day')}</span><span>${netSign}${formatCash(netDay)}</span>
        </div>
        <div class="dash-fin-row">
          <span style="color:var(--text-lo)">${t('dashboard.runway')}</span>
          <span>${runwayDays != null ? `~${runwayDays} ${t('topbar.cycle').toLowerCase()}` : '\u2014'}</span>
        </div>
        <button class="dash-link" onclick="nav('economy')">${t('dashboard.link_economy')}</button>
      </div>

      <!-- Runner-Übersicht -->
      <div class="panel">
        <div class="panel-title">${t('dashboard.runner_overview')}</div>
        <div style="display:grid; grid-template-columns:1fr 1fr; gap:6px; margin-bottom:10px">
          <div class="dash-stat"><span class="dash-stat-val" style="color:var(--green)">${activeCount}</span><span class="dash-stat-lbl">${t('dashboard.active')}</span></div>
          <div class="dash-stat"><span class="dash-stat-val" style="color:var(--red)">${arrestedCount}</span><span class="dash-stat-lbl">${t('dashboard.arrested')}</span></div>
          <div class="dash-stat"><span class="dash-stat-val" style="color:var(--amber)">${injuredCount}</span><span class="dash-stat-lbl">${t('dashboard.injured')}</span></div>
          <div class="dash-stat"><span class="dash-stat-val" style="color:var(--cyan)">${managerCount}</span><span class="dash-stat-lbl">${t('dashboard.manager')}</span></div>
        </div>
        <button class="dash-link" onclick="nav('runners')">${t('dashboard.link_runners')}</button>
      </div>

      <!-- Mini-Leaderboard -->
      <div class="panel">
        <div class="panel-title">${t('dashboard.leaderboard')}</div>
        <div id="dash-lb"></div>
        <div class="dash-divider" style="margin:8px 0"></div>
        <div class="dash-fin-row dash-sub"><span>${t('dashboard.territorial')}</span><span style="color:var(--amber)">${totalSectors > 0 ? `${Math.round(ownedSectors / totalSectors * 100)}%` : '\u2014'}</span></div>
        <button class="dash-link" onclick="nav('leaderboard')">${t('dashboard.link_leaderboard')}</button>
      </div>
    </div>

    <!-- Row 3: Gazette + Plan-Status -->
    <div style="display:grid; grid-template-columns:1fr 2fr; gap:16px">
      <div class="panel">
        <div class="panel-title">${t('dashboard.gazette')}</div>
        <div id="dash-gazette-preview"></div>
        <button class="dash-link" onclick="nav('gazette')">${t('dashboard.link_gazette')}</button>
      </div>
      <div class="panel">
        <div class="panel-title" style="display:flex; justify-content:space-between; align-items:center">
          <span>${t('dashboard.plan_status')}</span>
          <span style="display:flex; gap:4px">
            <button class="dash-export-btn" id="btn-export-plan" title="Plan als JSON exportieren">⬇</button>
            <button class="dash-export-btn" id="btn-copy-plan" title="Plan als JSON kopieren">📋</button>
          </span>
        </div>
        <div id="dash-plan-status"></div>
        <button class="dash-link" onclick="nav('map')">${t('dashboard.link_plan')}</button>
      </div>
    </div>

    <!-- Admin Debug-Snapshot -->
    ${isAdmin() ? `
    <div class="panel" id="dash-admin-export" style="margin-top:16px; border-color:rgba(252,163,17,0.3)">
      <div class="panel-title" style="display:flex; justify-content:space-between; align-items:center; color:var(--amber)">
        <span>🔧 ADMIN — Debug-Export</span>
      </div>
      <div style="font-size:12px; color:var(--text-lo); margin-bottom:10px">
        Kompletter Fixture-Snapshot inkl. Runner-Attribute, Buildings, Diplomatie, Sectors, Leaderboard.
        Ideal für Balancing, Bug-Reproduktion und Engine-Validierung.
      </div>
      <div style="display:flex; gap:8px; flex-wrap:wrap">
        <button class="dash-export-admin-btn" id="btn-debug-snapshot">⬇ Debug-Snapshot (.json)</button>
        <button class="dash-export-admin-btn" id="btn-debug-clipboard">📋 In Zwischenablage</button>
        <button class="dash-export-admin-btn" id="btn-export-fixture-raw">⬇ Raw Fixture (unverarbeitet)</button>
      </div>
    </div>` : ''}
  `;

  // Warnings
  const warnContainer = root.querySelector('#dash-warnings') as HTMLElement;
  for (const n of state.notifications) {
    const row = document.createElement('div');
    row.style.cssText = 'display:flex; align-items:flex-start; gap:8px; padding:4px 0; font-size:13px; border-bottom:1px solid var(--border); cursor:pointer;';
    row.innerHTML = `
      <span style="color:${n.level === 'crit' ? 'var(--red)' : n.level === 'warn' ? 'var(--amber)' : 'var(--blue)'}; font-size:16px; line-height:1.3">
        ${n.level === 'crit' ? '\uD83D\uDD34' : n.level === 'warn' ? '\uD83D\uDFE1' : '\uD83D\uDD35'}
      </span>
      <span>${n.text}</span>`;
    if (n.screen) row.addEventListener('click', () => { (window as any).nav(n.screen); });
    warnContainer.appendChild(row);
  }

  // Mini-Leaderboard from fixture
  const lb = root.querySelector('#dash-lb') as HTMLElement;
  const standings = (fixture.leaderboard ?? []).slice(0, 5);
  for (const c of standings) {
    const row = document.createElement('div');
    row.style.cssText = `display:flex; gap:8px; padding:3px 0; font-size:13px; ${c.isMe ? 'color:var(--cyan); font-weight:600;' : 'color:var(--text-mid);'}`;
    row.innerHTML = `<span style="width:18px; font-family:var(--mono)">#${c.rank}</span><span class="corp-link" data-corp="${c.name}" style="flex:1;cursor:pointer">${c.isMe ? `\u25B8 ${c.name} \u25C2` : c.name}</span><span style="font-family:var(--mono)">${formatCash(c.netWorth)}</span>`;
    lb.appendChild(row);
  }

  // Gazette preview
  const gazetteEl = root.querySelector('#dash-gazette-preview') as HTMLElement;
  const topHeadline = (fixture.gazette ?? [])[0];
  if (topHeadline) {
    // Translate the headline key (with {corp}/{sector} interpolation), matching
    // the gazette screen \u2014 otherwise the raw i18n key leaks into the preview.
    const dashHeadline = t(topHeadline.headline ?? '', topHeadline.headlineVars);
    gazetteEl.innerHTML = `<div class="dash-gazette-headline">\u201E${dashHeadline}\u201C</div>`;
  }

  // Plan status
  const planEl    = root.querySelector('#dash-plan-status') as HTMLElement;
  const plan      = fixture.player?.plan ?? [];
  const cycleNext = state.cycle + 1;
  if (plan.length > 0) {
    const runnerIds = new Set(plan.map(a => a.runner));
    planEl.innerHTML = `
      <div style="display:flex; align-items:center; gap:10px; margin-bottom:8px">
        <span style="color:var(--green); font-size:18px">\u2705</span>
        <span style="color:var(--text-hi)">${t('dashboard.plan_for_cycle', { cycle: cycleNext })} <strong>${t('dashboard.plan_uploaded')}</strong></span>
      </div>
      <div class="dash-fin-row dash-sub"><span>${t('dashboard.actions')}</span><span style="color:var(--cyan)">${plan.length}</span></div>
      <div class="dash-fin-row dash-sub"><span>${t('dashboard.runners_label')}</span><span style="color:var(--cyan)">${runnerIds.size}</span></div>
      <div class="dash-fin-row dash-sub"><span>${t('dashboard.deadline')}</span><span style="color:var(--amber)">${state.deadlineHour}:00 MEZ</span></div>`;
  } else {
    planEl.innerHTML = `
      <div style="display:flex; align-items:center; gap:10px; margin-bottom:8px">
        <span style="color:var(--amber); font-size:18px">\u26A0</span>
        <span style="color:var(--text-mid)">${t('dashboard.plan_for_cycle', { cycle: cycleNext })}</span>
      </div>
      <div class="dash-fin-row dash-sub"><span>${t('dashboard.actions')}</span><span style="color:var(--text-lo)">0</span></div>
      <div class="dash-fin-row dash-sub"><span>${t('dashboard.deadline')}</span><span style="color:var(--amber)">${state.deadlineHour}:00 MEZ</span></div>`;
  }

  // BEREIT button
  const readyBtn = root.querySelector('#btn-ready') as HTMLButtonElement;
  if (readyBtn && !isReady) {
    readyBtn.addEventListener('click', async () => {
      readyBtn.disabled = true;
      readyBtn.textContent = t('dashboard.ready_sending');
      try {
        await markReady(state.sessionId);
        readyBtn.textContent = t('dashboard.ready_confirmed');
        readyBtn.classList.add('confirmed');
        const panel = root.querySelector('#ready-panel') as HTMLElement;
        if (panel) panel.style.borderColor = 'rgba(52,211,153,0.3)';
      } catch {
        readyBtn.disabled = false;
        readyBtn.textContent = t('dashboard.ready_btn');
      }
    });
  }

  // Live readiness updates via WS custom event
  const readyPanel = root.querySelector('#ready-panel') as HTMLElement;
  if (readyPanel) {
    readyPanel.addEventListener('readiness-update', ((e: CustomEvent) => {
      const d = e.detail as { readyCount: number; totalPlayers: number; readyCorps: string[] };
      const countEl = root.querySelector('#ready-count');
      if (countEl) countEl.textContent = t('dashboard.ready_count', { ready: d.readyCount, total: d.totalPlayers });
      const barFill = root.querySelector('#ready-bar-fill') as HTMLElement;
      if (barFill) barFill.style.width = `${d.totalPlayers > 0 ? Math.round(d.readyCount / d.totalPlayers * 100) : 0}%`;
      const corpsEl = root.querySelector('#ready-corps');
      if (corpsEl) {
        const corps = (fixture.leaderboard ?? []).map(c => c.name);
        corpsEl.innerHTML = corps.map(name => {
          const ready = (d.readyCorps ?? []).includes(name);
          const isMe = name === state.corpName;
          return `<span class="ready-chip chip corp-link ${ready ? 'ready' : ''} ${isMe ? 'me' : ''}" data-corp="${name}" style="cursor:pointer">${ready ? '✓' : '○'} ${name}</span>`;
        }).join('');
      }
    }) as EventListener);
  }

  // Styles
  const style = document.createElement('style');
  style.textContent = `
    .dash-fin-row { display:flex; justify-content:space-between; padding:2px 0; font-size:13px; }
    .dash-fin-row span:first-child { color: var(--text-mid); }
    .dash-fin-row span:last-child  { font-family: var(--mono); font-size: 12px; }
    .dash-sub { padding-left: 10px; font-size: 12px !important; }
    .dash-sub span:first-child { color: var(--text-lo) !important; }
    .dash-divider { height:1px; background:linear-gradient(90deg,var(--line),transparent); margin:7px 0; }
    .dash-stat { position:relative; display:flex; flex-direction:column; align-items:center; background:var(--bg-tile); border:1px solid var(--line); padding:8px 6px; clip-path:polygon(0 0,calc(100% - 7px) 0,100% 7px,100% 100%,7px 100%,0 calc(100% - 7px)); }
    .dash-stat-val { font-family:var(--mono); font-size:22px; line-height:1.1; }
    .dash-stat-lbl { font-family:var(--display); font-size:9px; color:var(--text-lo); text-transform:uppercase; letter-spacing:1.5px; margin-top:3px; }
    /* ── KPI hero row + status bar (E5a, ported from FUI prototype) ── */
    .dash-statusbar { display:flex; flex-wrap:wrap; gap:1px; margin-bottom:14px; background:var(--line); border:1px solid var(--line); clip-path:polygon(0 0,calc(100% - 9px) 0,100% 9px,100% 100%,9px 100%,0 calc(100% - 9px)); }
    .dash-sb-cell { flex:1; min-width:128px; display:flex; flex-direction:column; gap:2px; padding:9px 16px; background:var(--bg-tile); }
    .dash-sb-lbl { font-family:var(--display); font-size:9px; letter-spacing:1.5px; text-transform:uppercase; color:var(--text-lo); }
    .dash-sb-val { font-family:var(--mono); font-size:15px; color:var(--text-hi); }
    .kpi-row { display:grid; grid-template-columns:repeat(4,1fr) auto auto; gap:14px; margin-bottom:16px; align-items:stretch; }
    .kpi { position:relative; background:var(--bg-tile); border:1px solid var(--line); padding:15px 16px; display:flex; flex-direction:column; justify-content:space-between; gap:6px; min-height:120px; clip-path:polygon(0 0,calc(100% - 9px) 0,100% 9px,100% 100%,9px 100%,0 calc(100% - 9px)); overflow:hidden; }
    .kpi .k-lbl { font-family:var(--display); font-size:10px; letter-spacing:2px; text-transform:uppercase; color:var(--text-lo); }
    .kpi .k-val { font-family:var(--mono); font-size:30px; line-height:1; color:var(--text-hi); }
    .kpi .k-sub { font-family:var(--mono); font-size:11px; color:var(--text-mid); }
    .kpi .spark { position:absolute; right:0; bottom:0; left:35%; height:20px; opacity:.16; background:linear-gradient(90deg,transparent,var(--cyan)); pointer-events:none; }
    .kpi.pos .k-val { color:var(--green); } .kpi.neg .k-val { color:var(--red); } .kpi.cyan .k-val { color:var(--cyan); } .kpi.amber .k-val { color:var(--amber); }
    .dial-tile { background:var(--bg-tile); border:1px solid var(--line); display:grid; place-items:center; padding:12px 20px 20px; clip-path:polygon(0 0,calc(100% - 9px) 0,100% 9px,100% 100%,9px 100%,0 calc(100% - 9px)); }
    @media (max-width:1100px) { .kpi-row { grid-template-columns:repeat(2,1fr); } }
    @media (max-width:560px) { .kpi-row { grid-template-columns:1fr; } }
    .dash-runner-warn { font-size:12px; color:var(--text-mid); padding:2px 0; }
    .dash-gazette-headline { font-family:var(--display); font-size:15px; font-weight:700; color:var(--text-hi); line-height:1.4; margin-bottom:10px; }
    .dash-link { display:inline-flex; align-items:center; gap:6px; margin-top:12px; background:none; border:none; color:var(--cyan-dim); font-family:var(--display); font-size:11px; font-weight:700; letter-spacing:1px; text-transform:uppercase; cursor:pointer; text-align:left; padding:0; transition:color 0.12s; }
    .dash-link::after { content:'▸'; }
    .dash-link:hover { color:var(--cyan); }
    /* .ready-btn → shared .btn.cyan (marker kept); confirmed state stays local */
    .ready-btn.confirmed { border-color:var(--green); color:var(--green); background:rgba(52,211,153,0.08); }
    .ready-bar { height:7px; background:var(--bg-void); border:1px solid var(--line); overflow:hidden; }
    .ready-bar-fill { height:100%; background:linear-gradient(90deg,var(--cyan-dim),var(--cyan)); box-shadow:0 0 8px var(--cyan); transition:width 0.3s ease; }
    /* .ready-chip → shared .chip (marker kept); ready/me states stay local */
    .ready-chip.ready { color:var(--green); border-color:rgba(52,211,153,0.4); }
    .ready-chip.me { color:var(--cyan); border-color:var(--line-hi); font-weight:600; }
    .dash-export-btn { background:none; border:1px solid var(--border); color:var(--text-lo); font-size:11px; padding:2px 6px; border-radius:3px; cursor:pointer; font-family:var(--mono); transition:all 0.12s; line-height:1; }
    .dash-export-btn:hover { color:var(--cyan); border-color:var(--cyan); }
    .dash-export-admin-btn { background:rgba(252,163,17,0.08); border:1px solid rgba(252,163,17,0.3); color:var(--amber); font-size:12px; padding:6px 14px; border-radius:4px; cursor:pointer; font-family:var(--mono); transition:all 0.12s; }
    .dash-export-admin-btn:hover { background:rgba(252,163,17,0.15); border-color:var(--amber); box-shadow:0 0 8px rgba(252,163,17,0.15); }
  `;
  root.appendChild(style);

  // ── Export button handlers ──────────────────────────────────────────
  function flashBtn(btn: HTMLElement, text: string): void {
    const orig = btn.textContent;
    btn.textContent = text;
    setTimeout(() => { btn.textContent = orig; }, 1500);
  }

  const btnExportResults = root.querySelector('#btn-export-results');
  btnExportResults?.addEventListener('click', (e) => { e.stopPropagation(); downloadJSON(buildResultsExport(), exportFilename('results')); });

  const btnCopyResults = root.querySelector('#btn-copy-results');
  btnCopyResults?.addEventListener('click', (e) => { e.stopPropagation(); copyJSON(buildResultsExport()); flashBtn(e.target as HTMLElement, '✓'); });

  const btnExportPlan = root.querySelector('#btn-export-plan');
  btnExportPlan?.addEventListener('click', (e) => { e.stopPropagation(); downloadJSON(buildPlanExport(), exportFilename('plan')); });

  const btnCopyPlan = root.querySelector('#btn-copy-plan');
  btnCopyPlan?.addEventListener('click', (e) => { e.stopPropagation(); copyJSON(buildPlanExport()); flashBtn(e.target as HTMLElement, '✓'); });

  // Admin debug exports
  const btnDebugSnapshot = root.querySelector('#btn-debug-snapshot');
  btnDebugSnapshot?.addEventListener('click', () => downloadJSON(buildDebugSnapshot(), exportFilename('debug')));

  const btnDebugClipboard = root.querySelector('#btn-debug-clipboard');
  btnDebugClipboard?.addEventListener('click', (e) => { copyJSON(buildDebugSnapshot()); flashBtn(e.target as HTMLElement, '✓ Kopiert'); });

  const btnFixtureRaw = root.querySelector('#btn-export-fixture-raw');
  btnFixtureRaw?.addEventListener('click', () => downloadJSON(fixture, exportFilename('fixture_raw')));

  root.addEventListener('click', (e) => {
    const link = (e.target as HTMLElement).closest('.corp-link') as HTMLElement | null;
    if (link?.dataset.corp) openCorpDossier(link.dataset.corp);
  });

  return root;
}
